'use client';

import { type ReactNode, useCallback, useEffect, useRef } from 'react';

export type ContentIframeProps = {
  id: string;
  title: string;
  src: string;
};

/**
 * ContentIframe embeds an iframe and dynamically resizes it to fit the remaining viewport height.
 *
 * @param props - Component props
 * @param props.id - DOM id of the iframe
 * @param props.title - Accessible title for the iframe
 * @param props.src - URL to load inside the iframe
 * @returns Iframe element with dynamic height
 */
export default function ContentIframe({ id, title, src }: ContentIframeProps): ReactNode {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const getElementOffsetTop = useCallback((el: HTMLElement | null): number => {
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const scrollTop = window.scrollY || window.pageYOffset;
    return Math.ceil(rect.top + scrollTop);
  }, []);

  const setDynamicHeight = useCallback((): void => {
    if (!iframeRef.current) return;
    const offsetTop = getElementOffsetTop(iframeRef.current);
    const newHeight = Math.max(0, Math.round(window.innerHeight - offsetTop));
    iframeRef.current.style.height = `${newHeight}px`;
  }, [getElementOffsetTop]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = (): void => {
      setDynamicHeight();
    };
    setDynamicHeight();

    const iframe = iframeRef.current;
    const handleLoad = (): void => {
      requestAnimationFrame(setDynamicHeight);
    };
    iframe?.addEventListener('load', handleLoad);

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });

    return (): void => {
      iframe?.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [setDynamicHeight]);

  return (
    <iframe
      ref={iframeRef}
      id={id}
      title={title}
      src={src}
      sandbox="allow-forms allow-scripts"
      referrerPolicy="no-referrer"
      style={{ border: 0, marginBottom: -4, width: '100%', height: '600px', display: 'block' }}
      loading="lazy"
      data-allow-advanced-mode="false"
    />
  );
}
